import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { EnrollPatientRequest, EnrollPatientResponse } from '@ctms/contracts';

import { patientsApi } from './patients.api.js';
import { patientQueryKeys } from './patients.query-keys.js';
import { trialQueryKeys } from '../trials/trials.query-keys.js';

export interface EnrollPatientVariables {
  patientId: number;
  request: EnrollPatientRequest;
}

/**
 * Mutation enrolling a patient into a clinical trial (REQ-PAT-009…014).
 * On success, invalidates:
 * - `trialQueryKeys.allEnrollments()` so the patient appears in "Pacjenci w badaniu"
 * - `patientQueryKeys.enrollments(patientId)` so the chart bar and visit schedule appear
 * - `trialQueryKeys.visits(trialId)` so the protocol catalog is cached
 */
export function useEnrollPatientMutation(): UseMutationResult<
  EnrollPatientResponse,
  Error,
  EnrollPatientVariables
> {
  const queryClient = useQueryClient();

  return useMutation<EnrollPatientResponse, Error, EnrollPatientVariables>({
    mutationFn: ({ patientId, request }) => patientsApi.enrollPatient(patientId, request),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: trialQueryKeys.allEnrollments() }),
        queryClient.invalidateQueries({
          queryKey: patientQueryKeys.enrollments(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: trialQueryKeys.visits(variables.request.trialId),
        }),
      ]);
    },
  });
}
